/**
 * Vertical config validation schemas.
 * Combines eligibility, ranking, and journey into a complete vertical configuration.
 */
import { z } from "zod";
import { VerticalSchemaDefinitionSchema } from "./attribute";
import { BrandingConfigSchema } from "./branding";
import { EligibilityConfigSchema } from "./eligibility";
import { environmentSchema } from "./environment";
import { JourneyConfigSchema } from "./journey";
import { RankingConfigSchema } from "./ranking";
// ============================================================================
// Config Types and Status
// ============================================================================
/** Config artifact types */
export const ConfigTypeSchema = z.enum(["schema", "eligibility", "ranking", "journey", "branding"]);
/** Config lifecycle states */
export const ConfigStatusSchema = z.enum(["draft", "live", "archived"]);
// ============================================================================
// Config Metadata
// ============================================================================
/** Config version record stored in D1 */
export const ConfigVersionRecordSchema = z
    .object({
    id: z.string().min(1),
    tenantId: z.string().min(1),
    verticalKey: z.string().min(1),
    environment: environmentSchema,
    version: z.number().int().min(1),
    configType: ConfigTypeSchema,
    r2Path: z.string().min(1),
    checksum: z.string().min(1),
    status: ConfigStatusSchema,
    changeSummary: z.string().max(500).optional(),
    createdBy: z.string().min(1),
    createdAt: z.string().datetime(),
    promotedAt: z.string().datetime().optional(),
    promotedBy: z.string().optional(),
})
    .strict();
/** Config pointer for R2 lookups */
export const ConfigPointerSchema = z
    .object({
    tenantId: z.string().min(1),
    environment: environmentSchema,
    verticalKey: z.string().min(1),
    configType: ConfigTypeSchema,
    version: z.number().int().min(1),
    r2Path: z.string().min(1),
    checksum: z.string().min(1),
    status: ConfigStatusSchema,
})
    .strict();
// ============================================================================
// Product Schema Definition
// ============================================================================
/** Product schema definition for a vertical */
export const ProductSchemaDefinitionSchema = VerticalSchemaDefinitionSchema.extend({
    displayName: z.string().min(1).max(100),
    description: z.string().max(500).optional(),
    icon: z.string().optional(),
    category: z.string().min(1).max(50).optional(),
    tags: z.array(z.string().min(1).max(30)).optional(),
}).strict();
// ============================================================================
// Vertical Configuration
// ============================================================================
/** Complete vertical configuration combining all config artifacts */
export const VerticalConfigSchema = z
    .object({
    verticalKey: z
        .string()
        .min(1)
        .regex(/^[a-z][a-z0-9-]*$/, "Vertical key must be kebab-case"),
    displayName: z.string().min(1).max(100),
    description: z.string().max(500).optional(),
    version: z.number().int().min(1),
    schema: VerticalSchemaDefinitionSchema,
    eligibility: EligibilityConfigSchema,
    ranking: RankingConfigSchema,
    journey: JourneyConfigSchema,
    branding: BrandingConfigSchema.optional(),
    metadata: z
        .object({
        createdAt: z.string().datetime(),
        updatedAt: z.string().datetime(),
        createdBy: z.string().optional(),
        updatedBy: z.string().optional(),
    })
        .strict()
        .optional(),
})
    .strict()
    .refine((data) => {
    // Validate that all config artifacts have matching verticalKey
    const key = data.verticalKey;
    return (data.schema.verticalKey === key &&
        data.eligibility.verticalKey === key &&
        data.ranking.verticalKey === key &&
        data.journey.verticalKey === key &&
        (data.branding === undefined ||
            data.branding.verticalKey === undefined ||
            data.branding.verticalKey === key));
}, {
    message: "All config artifacts must have matching verticalKey",
});
// ============================================================================
// Config Bundle
// ============================================================================
/** Complete config bundle for a vertical (runtime representation) */
export const ConfigBundleSchema = z
    .object({
    tenantId: z.string().min(1),
    verticalKey: z.string().min(1),
    environment: environmentSchema,
    schema: VerticalSchemaDefinitionSchema,
    eligibility: EligibilityConfigSchema,
    ranking: RankingConfigSchema,
    journey: JourneyConfigSchema,
    branding: BrandingConfigSchema.optional(),
    loadedAt: z.string().datetime(),
    checksums: z.record(ConfigTypeSchema, z.string()),
})
    .strict();
// ============================================================================
// Vertical List Item
// ============================================================================
/** Vertical list item for display */
export const VerticalListItemSchema = z
    .object({
    verticalKey: z.string(),
    displayName: z.string(),
    description: z.string().optional(),
    icon: z.string().optional(),
    category: z.string().optional(),
    tags: z.array(z.string()).optional(),
    productCount: z.number().int().min(0).optional(),
    status: ConfigStatusSchema,
    lastUpdated: z.string().datetime().optional(),
})
    .strict();
// ============================================================================
// Vertical Creation/Update Inputs
// ============================================================================
/** Input for creating a new vertical */
export const CreateVerticalInputSchema = z
    .object({
    verticalKey: z
        .string()
        .min(1)
        .max(50)
        .regex(/^[a-z][a-z0-9-]*$/, "Vertical key must be kebab-case"),
    displayName: z.string().min(1).max(100),
    description: z.string().max(500).optional(),
    category: z.string().min(1).max(50).optional(),
    tags: z.array(z.string().min(1).max(30)).max(10).optional(),
    icon: z.string().optional(),
})
    .strict();
/** Input for updating vertical metadata */
export const UpdateVerticalInputSchema = z
    .object({
    displayName: z.string().min(1).max(100).optional(),
    description: z.string().max(500).optional(),
    category: z.string().min(1).max(50).optional(),
    tags: z.array(z.string().min(1).max(30)).max(10).optional(),
    icon: z.string().optional(),
})
    .strict();
